// =============================================================
// WAVE 3 — Work-order detail (full execution surface).
//
// Opened from the WorkBoard list as a centered modal card matching the
// prototype (scripts/perpetuum-wo-detail.jsx + styles/perpetuum-desktop.css
// `.wo-*`). It is a REAL execution surface over the live /api/v1:
//
//   - HEADER: device + location breadcrumb, status chip, priority chip,
//     WO number / title, interval, due date.
//   - WORK TIMER: Start/Stop bound to /tasks/:id/work-times/{start,stop}; a
//     live ticking elapsed display for the running interval + the accumulated
//     total (sum of durationMs incl. the live one); the past entries list with
//     performer + duration.
//   - CHECKLIST: items from /tasks/:id/checklist-items — checkbox toggle (PATCH
//     done), add, delete; n/total progress; EmptyState when none.
//   - NOTES: edit task description via PATCH /tasks/:id with an explicit Save.
//   - SIGN-OFF: the existing LOTO-gated POST /tasks/:id/complete, surfaced as
//     the prototype's "Complete & sign off". It is DISABLED with the exact
//     reason the SERVER would block on — we mirror canCompleteWorkOrder /
//     lotoBlockReason (LOTO confirmed + every checklist item done + signed) so
//     the UI never lies; the server still re-checks and is the source of truth.
//
// Loading → Skeleton (Wave-1 primitive). Mutations invalidate the task +
// checklist + work-time queries so the surface reflects the server.
// =============================================================

import { useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  lotoBlockReason,
  type LotoBlockReason,
  type StatusKind,
  type Task,
  type TaskStatus,
} from "@perpetuum/core";
import {
  useAddChecklistItem,
  useCompleteTask,
  useDeleteChecklistItem,
  useStartWorkTime,
  useStopWorkTime,
  useTask,
  useTaskChecklist,
  useTaskWorkTimes,
  useUpdateChecklistItem,
  useUpdateTaskNotes,
} from "../lib/queries.js";
import { useAuth } from "../app/AuthContext.js";
import { useChrome } from "../app/ChromeContext.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Btn, Icon, ICONS, StatusChip } from "../components/primitives.js";
import { EmptyState } from "../components/EmptyState.js";
import { Skeleton } from "../components/Skeleton.js";

const STATUS_CHIP: Record<TaskStatus, [StatusKind, string]> = {
  open: ["info", "Open"],
  in_progress: ["warning", "In progress"],
  paused: ["paused", "Paused"],
  completed: ["success", "Completed"],
  cancelled: ["paused", "Cancelled"],
  postponed: ["paused", "Postponed"],
};
const PRIO_CHIP: Record<string, string> = {
  critical: "critical",
  high: "warning",
  normal: "paused",
  low: "paused",
};

/** Format a millisecond duration as HH:MM:SS (mono timer display). */
function fmtDuration(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export interface WoDetailProps {
  /** The list-shaped task that was opened (carries number/title/device for the header). */
  task: Task;
  onClose: () => void;
  /** Resolve a userId → display name (for the work-time performer + breadcrumb). */
  userName: (id: string) => string;
  /** Resolve a deviceId → display name (breadcrumb); undefined ⇒ show the id. */
  deviceName: (id: string) => string | undefined;
}

export function WoDetail({ task, onClose, userName, deviceName }: WoDetailProps) {
  const t = useScopedT();
  const { can } = useAuth();
  const { toast, confirm } = useChrome();

  // Full row (gate fields lotoConfirmed/signed + canonical description).
  const taskQ = useTask(task.id);
  const checklistQ = useTaskChecklist(task.id);
  const workTimesQ = useTaskWorkTimes(task.id);

  const addItem = useAddChecklistItem();
  const updItem = useUpdateChecklistItem();
  const delItem = useDeleteChecklistItem();
  const startTimer = useStartWorkTime();
  const stopTimer = useStopWorkTime();
  const saveNotes = useUpdateTaskNotes();
  const complete = useCompleteTask();

  const canWrite = can("tasks", "write");

  const row = taskQ.data;
  const checklist = checklistQ.data ?? [];
  const workTimes = workTimesQ.data ?? [];

  // ----- notes (controlled, with an explicit Save) -----
  const [notes, setNotes] = useState("");
  const notesSeeded = useRef(false);
  useEffect(() => {
    // Seed the textarea once the canonical description loads (don't clobber edits).
    if (row && !notesSeeded.current) {
      setNotes(row.description ?? "");
      notesSeeded.current = true;
    }
  }, [row]);
  const notesDirty = row != null && notes !== (row.description ?? "");

  // ----- add-checklist-item input -----
  const [newItem, setNewItem] = useState("");

  // ----- live elapsed ticker for the running work-time -----
  const runningEntry = useMemo(() => workTimes.find((w) => w.endedAt == null) ?? null, [workTimes]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!runningEntry) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [runningEntry]);

  const runningMs = runningEntry ? Math.max(0, now - new Date(runningEntry.startedAt).getTime()) : 0;
  // Accumulated total = closed durations + the live running interval.
  const totalMs = useMemo(
    () => workTimes.reduce((sum, w) => sum + (w.durationMs ?? 0), 0) + runningMs,
    [workTimes, runningMs],
  );

  // ----- checklist progress -----
  const doneCount = checklist.filter((c) => c.done).length;

  // ----- sign-off gate (mirror the server's canCompleteWorkOrder) -----
  const isCompleted = (row?.isCompleted ?? task.isCompleted) === true || (row?.status ?? task.status) === "completed";
  const gate = {
    lotoConfirmed: row?.lotoConfirmed === true,
    checklist: checklist.map((c) => c.done),
    signed: row?.signed === true,
  };
  // The reason the SERVER would block on, surfaced as a disabled-button hint so
  // the UI is honest (the server still re-checks on POST /complete).
  const blockReason: LotoBlockReason | null = lotoBlockReason(gate);
  const completeDisabled = !canWrite || complete.isPending || blockReason !== null;
  const BLOCK_KEY: Record<LotoBlockReason, string> = {
    loto_not_confirmed: "wo.block.loto",
    no_checklist: "wo.block.noChecklist",
    checklist_incomplete: "wo.block.checklist",
    not_signed: "wo.block.signed",
  };
  const blockHint = blockReason ? t(BLOCK_KEY[blockReason]) : undefined;

  const [chipKind, chipLabel] = STATUS_CHIP[(row?.status ?? task.status)] ?? ["paused", task.status];
  const devId = row?.deviceId ?? task.deviceId;
  const crumbDevice = devId ? deviceName(devId) ?? devId : t("wo.noDevice");
  const dueRaw = row?.dueDate ?? task.dueDate;
  const due = dueRaw ? new Date(dueRaw).toLocaleDateString() : "—";

  const onToggleCheck = async (itemId: string, done: boolean) => {
    try {
      await updItem.mutateAsync({ taskId: task.id, itemId, done });
    } catch {
      toast(t("login.err.network_error"), "error");
    }
  };
  const onAddItem = async () => {
    const label = newItem.trim();
    if (!label) return;
    try {
      await addItem.mutateAsync({ taskId: task.id, label });
      setNewItem("");
    } catch {
      toast(t("login.err.network_error"), "error");
    }
  };
  const onDeleteItem = async (itemId: string) => {
    try {
      await delItem.mutateAsync({ taskId: task.id, itemId });
    } catch {
      toast(t("login.err.network_error"), "error");
    }
  };
  const onSaveNotes = async () => {
    try {
      await saveNotes.mutateAsync({ taskId: task.id, description: notes });
      toast(t("wo.notesSaved"), "success");
    } catch {
      toast(t("login.err.network_error"), "error");
    }
  };
  const onToggleTimer = async () => {
    try {
      if (runningEntry) {
        await stopTimer.mutateAsync(task.id);
        toast(t("wo.timerStopped"), "info");
      } else {
        await startTimer.mutateAsync(task.id);
        toast(t("wo.timerStarted"), "success");
      }
    } catch (e) {
      if (e instanceof ApiError && e.code === "already_running") {
        toast(t("wo.timerAlreadyRunning"), "warning");
      } else {
        toast(t("login.err.network_error"), "error");
      }
    }
  };
  const onComplete = async () => {
    const ok = await confirm({
      title: t("wo.signOff"),
      body: `${task.title} — ${task.number ?? task.id.slice(0, 8)}`,
      confirmLabel: t("wo.signOff"),
    });
    if (!ok) return;
    try {
      await complete.mutateAsync(task.id);
      toast(t("workboard.completed"), "success");
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === "loto_blocked") {
        toast(t("workboard.loto"), "warning");
      } else {
        toast(t("login.err.network_error"), "error");
      }
    }
  };

  const loading = taskQ.isLoading || checklistQ.isLoading || workTimesQ.isLoading;

  return (
    <div className="wo-scrim" onClick={onClose}>
      <div
        className="wo-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${task.number ?? task.title}`}
        data-testid="wo-detail"
      >
        {/* HEADER */}
        <div className="wo-head">
          <div className="wo-head-main">
            <div className="wo-crumb">
              {crumbDevice} · {row?.locationId ? row.locationId : t("wo.noLocation")}
            </div>
            <div className="wo-title-row">
              <StatusChip kind={chipKind}>{chipLabel}</StatusChip>
              {(row?.priority ?? task.priority) && (
                <span className={`prio-chip ${PRIO_CHIP[(row?.priority ?? task.priority)!] ?? "paused"}`}>
                  {row?.priority ?? task.priority}
                </span>
              )}
            </div>
            <h2 className="wo-title">{row?.title ?? task.title}</h2>
            <div className="wo-sub">
              {task.number ?? task.id.slice(0, 8)} · {t("wo.due")}: {due}
            </div>
          </div>
          <button className="dc-close" onClick={onClose} aria-label={t("common.close")}>
            <Icon d={ICONS.x} size={20} />
          </button>
        </div>

        <div className="wo-body">
          <div className="wo-main">
            {/* WORK TIMER */}
            <div className={`wo-timer ${runningEntry ? "run" : ""}`}>
              <div className="wo-timer-l">
                <span className="wo-timer-lbl">{runningEntry ? t("wo.timer.running") : t("wo.timer.label")}</span>
                <span
                  className="wo-timer-val"
                  role="timer"
                  aria-live="polite"
                  aria-label={t("wo.timer.elapsed", { time: fmtDuration(runningEntry ? runningMs : totalMs) })}
                  data-testid="wo-timer-val"
                >
                  {fmtDuration(runningEntry ? runningMs : totalMs)}
                </span>
              </div>
              <Btn
                variant={runningEntry ? "stop" : "go"}
                size="core"
                disabled={!canWrite || startTimer.isPending || stopTimer.isPending}
                onClick={() => void onToggleTimer()}
                data-testid="wo-timer-toggle"
                aria-label={runningEntry ? t("wo.timer.stop") : t("wo.timer.start")}
              >
                <Icon d={runningEntry ? ICONS.stopSquare : ICONS.play} size={16} />
                {runningEntry ? t("wo.timer.stop") : t("wo.timer.start")}
              </Btn>
            </div>

            {/* accumulated total + past entries */}
            <div className="wo-timer-total" data-testid="wo-timer-total">
              {t("wo.timer.total")}: <strong>{fmtDuration(totalMs)}</strong>
            </div>
            {workTimes.length > 0 && (
              <ul className="wo-wt-list" aria-label={t("wo.timer.entries")}>
                {workTimes.map((w) => (
                  <li key={w.id} className="wo-wt-row">
                    <Icon d={ICONS.user} size={14} />
                    <span className="wo-wt-who">{w.userId ? userName(w.userId) : "—"}</span>
                    <span className="wo-wt-dur">
                      {w.endedAt == null ? t("wo.timer.running") : fmtDuration(w.durationMs ?? 0)}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {/* CHECKLIST */}
            <div className="dc-sec">
              {t("wo.checklist")}
              <span className="ad-sec-count" data-testid="wo-check-progress">
                {doneCount}/{checklist.length}
              </span>
            </div>
            {loading && <Skeleton variant="text" count={3} />}
            {!loading && checklist.length === 0 && (
              <EmptyState icon={ICONS.list} title={t("wo.checklist.empty")} compact />
            )}
            {!loading && checklist.length > 0 && (
              <div className="wo-checks">
                {checklist.map((item) => (
                  <div key={item.id} className="wo-check-line">
                    <button
                      type="button"
                      className={`wo-check ${item.done ? "on" : ""}`}
                      role="checkbox"
                      aria-checked={item.done}
                      aria-label={item.label}
                      disabled={!canWrite || updItem.isPending}
                      data-testid={`wo-check-${item.id}`}
                      onClick={() => void onToggleCheck(item.id, !item.done)}
                    >
                      <span className="wo-cbox" aria-hidden="true">
                        {item.done && <Icon d={ICONS.check} size={13} sw={3} />}
                      </span>
                      {item.label}
                    </button>
                    {canWrite && (
                      <button
                        type="button"
                        className="wo-check-del"
                        aria-label={t("wo.checklist.delete", { label: item.label })}
                        data-testid={`wo-check-del-${item.id}`}
                        onClick={() => void onDeleteItem(item.id)}
                      >
                        <Icon d={ICONS.trash} size={15} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {canWrite && (
              <div className="wo-check-add">
                <input
                  type="text"
                  value={newItem}
                  placeholder={t("wo.checklist.addPlaceholder")}
                  aria-label={t("wo.checklist.add")}
                  data-testid="wo-check-add-input"
                  onChange={(e) => setNewItem(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void onAddItem();
                  }}
                />
                <Btn
                  variant="secondary"
                  size="sm"
                  disabled={!newItem.trim() || addItem.isPending}
                  data-testid="wo-check-add"
                  onClick={() => void onAddItem()}
                >
                  <Icon d={ICONS.plus} size={15} />
                  {t("wo.checklist.add")}
                </Btn>
              </div>
            )}

            {/* NOTES */}
            <div className="dc-sec">{t("wo.notes")}</div>
            <textarea
              className="wo-notes"
              value={notes}
              placeholder={t("wo.notes.placeholder")}
              aria-label={t("wo.notes")}
              data-testid="wo-notes"
              disabled={!canWrite}
              onChange={(e) => setNotes(e.target.value)}
            />
            {canWrite && (
              <div className="wo-notes-foot">
                <Btn
                  variant="secondary"
                  size="sm"
                  disabled={!notesDirty || saveNotes.isPending}
                  data-testid="wo-notes-save"
                  onClick={() => void onSaveNotes()}
                >
                  {t("wo.notes.save")}
                </Btn>
              </div>
            )}
          </div>

          {/* SIDE — details */}
          <div className="wo-side">
            <div className="dc-sec">{t("wo.details")}</div>
            <div className="dc-kv">
              <span className="k">{t("wo.device")}</span>
              <span className="v">{crumbDevice}</span>
            </div>
            <div className="dc-kv">
              <span className="k">{t("wo.service")}</span>
              <span className="v">{row?.serviceName ?? task.serviceName ?? "—"}</span>
            </div>
            <div className="dc-kv">
              <span className="k">{t("wo.priority")}</span>
              <span className="v">{row?.priority ?? task.priority ?? "normal"}</span>
            </div>
            <div className="dc-kv">
              <span className="k">{t("wo.due")}</span>
              <span className="v">{due}</span>
            </div>
            <div className="dc-kv">
              <span className="k">{t("wo.performer")}</span>
              <span className="v">
                {(row?.assignedUserIds ?? task.assignedUserIds ?? []).length > 0
                  ? (row?.assignedUserIds ?? task.assignedUserIds ?? []).map(userName).join(", ")
                  : t("wo.unassigned")}
              </span>
            </div>
            <div className="gd-flag">
              <Icon d={ICONS.shield} size={15} />
              {t("workboard.loto")}
            </div>
          </div>
        </div>

        {/* FOOT — sign-off */}
        <div className="wo-foot">
          <span style={{ flex: 1 }} />
          <Btn variant="ghost" size="core" onClick={onClose}>
            {t("common.close")}
          </Btn>
          {!isCompleted && (
            <Btn
              variant="go"
              size="core"
              disabled={completeDisabled}
              title={blockHint}
              data-testid="wo-signoff"
              onClick={() => void onComplete()}
            >
              <Icon d={ICONS.check} size={16} />
              {t("wo.signOff")}
            </Btn>
          )}
        </div>
      </div>
    </div>
  );
}
